import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const SLUG = "bitcoin-mistakes-2017-2025";
const AMOUNT_SATS = 1000;

const TOKEN_KEY = `gyb_premium_${SLUG}`;
const CHARGE_KEY = `gyb_charge_${SLUG}`;

function decodeJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function tokenValidForThisSlug(token) {
  const p = decodeJwtPayload(token);
  if (!p) return false;
  if (p.slug !== SLUG) return false;
  if (p.exp && Date.now() / 1000 > p.exp) return false;
  return true;
}

export default function PremiumBitcoinMistakes() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [status, setStatus] = useState("idle"); // idle | creating | waiting | paid | error
  const [err, setErr] = useState("");

  // ✅ IMPORTANT: persist the charge so refresh doesn't break the flow
  const [charge, setCharge] = useState(() => {
    try {
      const raw = localStorage.getItem(CHARGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [invoice, setInvoice] = useState(() => {
    const inv =
      charge?.lightning_invoice?.payreq ||
      charge?.lightning_invoice ||
      charge?.ln_invoice ||
      charge?.invoice ||
      "";
    return inv || "";
  });

  const unlocked = useMemo(() => tokenValidForThisSlug(token), [token]);

  function persistCharge(data) {
    setCharge(data);
    localStorage.setItem(CHARGE_KEY, JSON.stringify(data));

    const inv =
      data?.lightning_invoice?.payreq ||
      data?.lightning_invoice ||
      data?.ln_invoice ||
      data?.invoice ||
      "";

    setInvoice(inv || "");
  }

  function resetInvoice() {
    localStorage.removeItem(CHARGE_KEY);
    setCharge(null);
    setInvoice("");
    setErr("");
    setStatus("idle");
  }

  // Poll for payment ONLY for the stored charge.id
  useEffect(() => {
    if (charge?.id && status === "waiting") {
      let cancelled = false;

      async function poll() {
        try {
          for (let i = 0; i < 180; i++) {
            if (cancelled) return;
            await new Promise((r) => setTimeout(r, 2000));

            const r2 = await fetch(
              `/.netlify/functions/checkCharge?id=${encodeURIComponent(charge.id)}`
            );
            const j2 = await r2.json().catch(() => ({}));

            // Your checkCharge returns { data: {...} }
            const data = j2?.data || j2?.data?.data || j2;
            const s = String(data?.status || "").toLowerCase();

            if (s === "paid" || s === "confirmed") {
              // Ask server to issue token
              const r3 = await fetch("/.netlify/functions/issueToken", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  chargeId: charge.id,
                  slug: SLUG,
                  requiredSats: AMOUNT_SATS,
                }),
              });

              const j3 = await r3.json().catch(() => ({}));

              if (!r3.ok) {
                throw new Error(j3?.error || j3?.details?.message || "Could not issue token.");
              }

              localStorage.setItem(TOKEN_KEY, j3.token);
              setToken(j3.token);
              setStatus("paid");
              return;
            }
          }

          throw new Error("Payment not detected yet. If you paid, keep this tab open a bit longer.");
        } catch (e) {
          setErr(e.message || "Unlock failed.");
          setStatus("error");
        }
      }

      poll();
      return () => {
        cancelled = true;
      };
    }
  }, [charge?.id, status]);

  async function startUnlock() {
    setErr("");

    // ✅ KEY FIX: if we already have a charge, DO NOT create a new one
    if (charge?.id) {
      setStatus("waiting");
      return;
    }

    setStatus("creating");
    setCharge(null);
    setInvoice("");

    try {
      const r1 = await fetch("/.netlify/functions/createCharge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amountSats: AMOUNT_SATS, slug: SLUG }),
      });

      const j1 = await r1.json().catch(() => ({}));

      if (!r1.ok) {
        throw new Error(j1?.error || j1?.details?.message || "Could not create charge.");
      }

      const data = j1?.data;
      if (!data?.id) throw new Error("Charge created but missing id.");

      persistCharge(data);
      setStatus("waiting");
    } catch (e) {
      setErr(e.message || "Charge creation failed.");
      setStatus("error");
    }
  }

  function copyInvoice() {
    if (!invoice) return;
    navigator.clipboard.writeText(invoice);
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top,rgba(255,106,0,0.18),transparent_60%)]" />
      <main className="relative mx-auto max-w-3xl px-6 pb-24 pt-14">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="text-sm text-zinc-400 hover:text-zinc-200">
            ← Back to Home
          </Link>
          <div className="text-xs rounded-full border border-white/10 bg-white/5 px-3 py-1 text-zinc-300">
            Premium • {AMOUNT_SATS} sats
          </div>
        </div>

        <header className="mt-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl bg-gradient-to-r from-orange-500 to-rose-600 bg-clip-text text-transparent">
            5 Bitcoin Mistakes (2017–2025)
          </h1>
          <p className="mt-4 text-zinc-300">
            Real mistakes, real fixes — keep more sats and protect your stack.
          </p>
        </header>

        {!unlocked ? (
          <section className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6">
            <h2 className="text-xl font-bold text-zinc-100">Unlock instantly</h2>
            <p className="mt-2 text-zinc-300">
              Pay {AMOUNT_SATS} sats via Lightning. The page unlocks automatically when payment confirms.
            </p>

            <div className="mt-5 flex flex-col gap-3">
              <button
                onClick={startUnlock}
                disabled={status === "creating" || status === "waiting"}
                className="rounded-xl bg-gradient-to-r from-orange-500 to-rose-600 px-4 py-3 font-semibold text-white disabled:opacity-60"
              >
                {status === "creating"
                  ? "Creating invoice..."
                  : status === "waiting"
                  ? "Waiting for payment..."
                  : `Unlock for ${AMOUNT_SATS} sats`}
              </button>

              {charge?.hosted_checkout_url ? (
                <a
                  href={charge.hosted_checkout_url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-center text-zinc-200 hover:bg-black/40"
                >
                  Open Checkout
                </a>
              ) : null}

              {invoice ? (
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                  <div className="text-xs text-zinc-400">Lightning invoice</div>
                  <div className="mt-2 break-all text-sm text-zinc-200">{invoice}</div>
                  <button
                    onClick={copyInvoice}
                    className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm hover:bg-white/10"
                  >
                    Copy invoice
                  </button>
                </div>
              ) : null}

              {/* ✅ SUPER IMPORTANT: Reset so you can restart cleanly */}
              {charge?.id ? (
                <button
                  onClick={resetInvoice}
                  className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm hover:bg-white/10"
                >
                  Reset invoice (start over)
                </button>
              ) : null}

              {err ? <div className="text-sm text-rose-400">{err}</div> : null}

              <div className="text-xs text-zinc-500">
                Tip: don’t refresh or click Unlock again after paying — keep this tab open so it can detect payment.
              </div>
            </div>
          </section>
        ) : (
          <article className="mt-10 space-y-8 leading-relaxed">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-2xl font-bold">Mistake #1 — Treating exchanges like banks</h2>
              <p className="mt-3 text-zinc-300">
                You buy on an exchange and tell yourself you’ll withdraw “later”. Later becomes weeks… months…
                and your savings is sitting on someone else’s database.
              </p>
              <p className="mt-3 text-zinc-300">
                <b>Fix:</b> Set a rule: withdraw same day (or weekly max). Hot wallet for spending. Cold storage for savings.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-2xl font-bold">Mistake #2 — Chasing alts to “get more BTC later”</h2>
              <p className="mt-3 text-zinc-300">
                Most people don’t rotate into alts and rotate back perfectly. They round-trip gains to zero.
              </p>
              <p className="mt-3 text-zinc-300">
                <b>Fix:</b> Bitcoin is the base. If you “play”, cap it small and never touch your BTC stack.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-2xl font-bold">Mistake #3 — No UTXO plan (death by fees later)</h2>
              <p className="mt-3 text-zinc-300">
                Lots of tiny withdrawals create a dust wallet. When fees spike, moving becomes painful.
              </p>
              <p className="mt-3 text-zinc-300">
                <b>Fix:</b> Prefer fewer, larger withdrawals. Consolidate intentionally when fees are low.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-2xl font-bold">Mistake #4 — Overcomplicating security</h2>
              <p className="mt-3 text-zinc-300">
                The biggest loss is not hackers. It’s messy backups, forgotten passphrases, rushed mistakes.
              </p>
              <p className="mt-3 text-zinc-300">
                <b>Fix:</b> One cold wallet. One spending wallet. Test recovery once with a small amount.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-2xl font-bold">Mistake #5 — No real-world plan (spend / travel / inheritance)</h2>
              <p className="mt-3 text-zinc-300">
                If only you can access it, it’s not sovereign — it’s fragile.
              </p>
              <p className="mt-3 text-zinc-300">
                <b>Fix:</b> Write a one-page ops plan: buy, withdraw, storage, emergency cashflow, recovery instructions.
              </p>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
